/*
 * Copyright (C) 2022-2025 - Jeci SARL - https://jeci.fr
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
 */

import rocketChatAuthService from "@/services/RocketChatAuthService";

/**
 * Service for interacting with Rocket.Chat REST API.
 * Handles rooms, messages, and subscriptions.
 */
class RocketChatService {
  /**
   * Make an authenticated API request to Rocket.Chat.
   *
   * @param {string} endpoint - API endpoint (e.g., '/api/v1/channels.list')
   * @param {Object} options - Fetch options
   * @returns {Promise<Object>}
   */
  async request(endpoint, options = {}) {
    const creds = await rocketChatAuthService.getCredentials();
    if (!creds) {
      throw new Error("Not authenticated to Rocket.Chat");
    }

    const baseUrl = rocketChatAuthService.getServerUrl();
    const url = `${baseUrl}${endpoint}`;

    const headers = {
      "Content-Type": "application/json",
      "X-Auth-Token": creds.authToken,
      "X-User-Id": creds.userId,
      ...options.headers,
    };

    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Check content type before parsing
    const contentType = response.headers.get("content-type");
    if (!contentType || !contentType.includes("application/json")) {
      const text = await response.text();
      console.error(
        `[RocketChatService] Expected JSON but got ${contentType}:`,
        text.substring(0, 200),
      );
      throw new Error(
        `Invalid response from Rocket.Chat: expected JSON, got ${contentType}`,
      );
    }

    const data = await response.json();

    if (!response.ok || data.success === false) {
      const error = new Error(data.error || `HTTP ${response.status}`);
      error.status = response.status;
      error.data = data;
      throw error;
    }

    return data;
  }

  /**
   * Get list of subscribed rooms (channels, groups, DMs).
   *
   * @returns {Promise<Array>}
   */
  async getSubscriptions() {
    const data = await this.request("/api/v1/subscriptions.get");
    return data.update || [];
  }

  /**
   * Get list of private groups the user is member of.
   *
   * @returns {Promise<Array>}
   */
  async getGroups() {
    const data = await this.request("/api/v1/groups.list");
    return data.groups || [];
  }

  /**
   * Get information about a specific room.
   *
   * @param {string} roomId - The room ID
   * @returns {Promise<Object>}
   */
  async getRoomInfo(roomId) {
    const data = await this.request(`/api/v1/rooms.info?roomId=${roomId}`);
    return data.room;
  }

  /**
   * Get message history for a room.
   *
   * @param {string} roomId - The room ID
   * @param {Object} options - Options
   * @param {number} options.count - Number of messages to fetch (default: 50)
   * @param {string} options.oldest - Oldest message timestamp
   * @param {string} options.latest - Latest message timestamp
   * @returns {Promise<Array>}
   */
  async getMessages(roomId, options = {}) {
    const params = new URLSearchParams({
      roomId,
      count: options.count || 50,
    });

    if (options.oldest) {
      params.append("oldest", options.oldest);
    }
    if (options.latest) {
      params.append("latest", options.latest);
    }

    const data = await this.request(
      `/api/v1/groups.history?${params.toString()}`,
    );
    return data.messages || [];
  }

  /**
   * Send a message to a room.
   *
   * @param {string} roomId - The room ID
   * @param {string} text - The message text
   * @returns {Promise<Object>}
   */
  async sendMessage(roomId, text) {
    const data = await this.request("/api/v1/chat.postMessage", {
      method: "POST",
      body: JSON.stringify({
        roomId,
        text,
      }),
    });
    return data.message;
  }

  /**
   * Mark room as read.
   *
   * @param {string} roomId - The room ID
   * @returns {Promise<void>}
   */
  async markAsRead(roomId) {
    await this.request("/api/v1/subscriptions.read", {
      method: "POST",
      body: JSON.stringify({ rid: roomId }),
    });
  }

  /**
   * Get unread count for all subscriptions.
   *
   * @returns {Promise<Object>} Map of roomId -> unread count
   */
  async getUnreadCounts() {
    const subscriptions = await this.getSubscriptions();
    const counts = {};

    for (const sub of subscriptions) {
      if (sub.unread > 0) {
        counts[sub.rid] = sub.unread;
      }
    }

    return counts;
  }

  /**
   * Get user presence (online status).
   *
   * @param {string} userId - The user ID
   * @returns {Promise<string>} Status: 'online', 'away', 'busy', 'offline'
   */
  async getUserPresence(userId) {
    try {
      const data = await this.request(
        `/api/v1/users.getPresence?userId=${userId}`,
      );
      return data.presence;
    } catch {
      return "offline";
    }
  }

  /**
   * Set user presence.
   *
   * @param {string} status - Status: 'online', 'away', 'busy', 'offline'
   * @returns {Promise<void>}
   */
  async setUserPresence(status) {
    await this.request("/api/v1/users.setStatus", {
      method: "POST",
      body: JSON.stringify({ status }),
    });
  }

  /**
   * Get room members.
   *
   * @param {string} roomId - The room ID
   * @returns {Promise<Array>}
   */
  async getRoomMembers(roomId) {
    const data = await this.request(`/api/v1/groups.members?roomId=${roomId}`);
    return data.members || [];
  }

  /**
   * Upload a file to a room.
   *
   * @param {string} roomId - The room ID
   * @param {File} file - The file to upload
   * @param {string} description - Optional description
   * @returns {Promise<Object>}
   */
  async uploadFile(roomId, file, description = "") {
    const creds = await rocketChatAuthService.getCredentials();
    if (!creds) {
      throw new Error("Not authenticated to Rocket.Chat");
    }

    const formData = new FormData();
    formData.append("file", file);
    if (description) {
      formData.append("description", description);
    }

    const baseUrl = rocketChatAuthService.getServerUrl();
    const response = await fetch(`${baseUrl}/api/v1/rooms.upload/${roomId}`, {
      method: "POST",
      headers: {
        "X-Auth-Token": creds.authToken,
        "X-User-Id": creds.userId,
      },
      body: formData,
    });

    const data = await response.json();

    if (!response.ok || data.success === false) {
      throw new Error(data.error || "Failed to upload file");
    }

    return data;
  }
}

export default new RocketChatService();
